import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  Activity,
  ArrowRight,
  Bell,
  Cloud,
  Factory,
  Gauge,
  History,
  Image as ImageIcon,
  KeyRound,
  Layers,
  LockKeyhole,
  Maximize2,
  Network,
  Archive,
  PackageCheck,
  Power,
  Radio,
  RefreshCcw,
  RotateCcw,
  Rocket,
  ShieldCheck,
  ShieldOff,
  TimerReset,
  Thermometer,
  Truck,
  Users,
  Warehouse,
  ChevronRight,
  WifiOff,
} from "lucide-react";
import apiIntegrationsImg from "@/assets/alcatraz/api-integrations.jpg";
import dashboardImg from "@/assets/alcatraz/dashboard-dark.png";
import firmwareReleaseActionsImg from "@/assets/alcatraz/firmware-release-actions.jpg";
import firmwareReleaseDetailImg from "@/assets/alcatraz/firmware-release-detail.jpg";
import firmwareReleaseListImg from "@/assets/alcatraz/firmware-release-list.jpg";
import firmwareReleaseRolloutImg from "@/assets/alcatraz/firmware-release-rollout.jpg";
import pushNotificationTvImg from "@/assets/alcatraz/push-notification-tv.jpg";
import { MonitorMockup } from "@/components/MonitorMockup";
import { RevealSection, revealClass, useReveal } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

type SiteMessages = ReturnType<typeof useSite>["messages"];

const firmwareReleaseImages = [
  firmwareReleaseDetailImg,
  firmwareReleaseActionsImg,
  firmwareReleaseRolloutImg,
  firmwareReleaseListImg,
] as const;

const fleetHealthPreviewIssues = [
  {
    model: "TV5 Ultra",
    issue: "weakWifi",
    reading: "−82 dBm",
    ageMinutes: 12,
    icon: WifiOff,
    critical: true,
  },
  {
    model: "TV6",
    issue: "temperature",
    reading: "84°C",
    ageMinutes: 28,
    icon: Thermometer,
    critical: true,
  },
  {
    model: "TV5",
    issue: "weakWifi",
    reading: "−74 dBm",
    ageMinutes: 41,
    icon: WifiOff,
    critical: false,
  },
] as const;

// Keep the page renderable when source catalogs have new preview copy but the generated
// runtime has not been refreshed yet. Generated locale messages override these fallbacks.
const alcatrazPreviewFallbackMessages = {
  alcatrazFleetPreviewUpdated: "Updated just now",
  alcatrazFleetPreviewLive: "Live overview",
  alcatrazFleetPreviewNeedsAttention: "Needs attention",
  alcatrazFleetPreviewCallFirst: "Call first",
  alcatrazFleetPreviewKeepWatch: "Keep an eye on",
  alcatrazFleetPreviewHealthy: "Healthy",
  alcatrazFleetPreviewMonitored: "Monitored",
  alcatrazFleetPreviewIssueTypes: "Issue types",
  alcatrazFleetPreviewWeakWifi: "Weak Wi-Fi",
  alcatrazFleetPreviewTemperature: "Temperature",
  alcatrazFleetPreviewQueue: "Priority device queue",
  alcatrazFleetPreviewCriticalFirst: "Critical conditions first",
  alcatrazFleetPreviewShown: "Showing {count}",
  alcatrazFleetPreviewHighTemperature: "High temperature",
  alcatrazFleetPreviewWatch: "Monitor",
  alcatrazFleetPreviewAwaiting: "{count} issues awaiting confirmation",
  alcatrazPreviewMinutesAgo: "{minutes} min ago",
  alcatrazPreviewIllustrativeData: "Illustrative data",
  alcatrazInspectorPreviewOnline: "Online",
  alcatrazInspectorPreviewCurrentState: "Current state",
  alcatrazInspectorPreviewActivity: "Activity",
  alcatrazInspectorPreviewCondition: "Device condition",
  alcatrazInspectorPreviewLastChecked: "Checked {minutes} min ago",
  alcatrazInspectorPreviewAllEvents: "All events",
  alcatrazInspectorPreviewCommands: "Commands",
  alcatrazInspectorPreviewNewest: "Newest first",
  alcatrazInspectorPreviewToday: "Today",
  alcatrazInspectorPreviewYesterday: "Yesterday",
  alcatrazInspectorPreviewThisWeek: "This week",
  alcatrazInspectorPreviewLastWeek: "Last week",
  alcatrazInspectorPreviewEarlier: "Earlier",
  alcatrazInspectorPreviewSupportNote: "Support note: intermittent buffering",
  alcatrazInspectorPreviewFirmwareChange: "Firmware 1.8.51 → 1.9.1",
  alcatrazInspectorPreviewOtaCompleted: "OTA download completed",
  alcatrazInspectorPreviewCommandBy: "Command · John Doe",
  alcatrazInspectorPreviewPrivateNoteBy: "Private note · John Doe",
  alcatrazInspectorPreviewDeviceChangeApi: "Device change · API key · rollout-bot",
  alcatrazInspectorPreviewOtaMetrics: "Version 1.9.0 · 45.78 MB/s · 1 min 18 sec",
  alcatrazInspectorPreviewWaiting: "Waiting for response",
  alcatrazInspectorPreviewCompleted: "Completed",
  alcatrazInspectorPreviewFooter: "Commands, changes, notes and OTA events",
  alcatrazInspectorPreviewIllustrative: "Sample history",
  alcatrazInspectorPreviewAlt:
    "Illustrative Alcatraz device inspector showing current state and activity history.",
} as const;

type AlcatrazPreviewMessages = SiteMessages & typeof alcatrazPreviewFallbackMessages;

function withAlcatrazPreviewFallbacks(messages: SiteMessages): AlcatrazPreviewMessages {
  return { ...alcatrazPreviewFallbackMessages, ...messages } as AlcatrazPreviewMessages;
}

type LockModeId = "hard" | "soft" | "app";

type LockMode = {
  id: LockModeId;
  icon: typeof ShieldOff;
  title: string;
  body: string;
  tvBadge: string;
  tvTitle: string;
  tvBody: string;
  tvStatus: string;
};

const LOCK_MODE_IDS: LockModeId[] = ["hard", "soft", "app"];
const LOCK_MODE_AUTO_CYCLE_MS = 6500;

export function AlcatrazPage() {
  const { messages, site, href } = useSite();
  const [selectedLockMode, setSelectedLockMode] = useState<LockModeId>("hard");
  const [isLockModeAutoCycleActive, setIsLockModeAutoCycleActive] = useState(true);
  const home = localizeUrl("/", site.locale, href);
  const otaLink = localizeUrl("/solutions/ota-updates", site.locale, href);
  const demoCta = localizeUrl("/contacts?inquiry=demo#inquiry-form", site.locale, href);
  const salesCta = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);
  const startHeroCta = localizeUrl(
    "/solutions/alcatraz-dms/get-started?source=alcatraz-hero",
    site.locale,
    href,
  );
  const startPricingCta = localizeUrl(
    "/solutions/alcatraz-dms/get-started?source=alcatraz-pricing",
    site.locale,
    href,
  );
  const startFinalCta = localizeUrl(
    "/solutions/alcatraz-dms/get-started?source=alcatraz-final",
    site.locale,
    href,
  );

  const heroStats = [
    { icon: Users, label: messages.alcatrazHeroStatNetworks },
    { icon: Cloud, label: messages.alcatrazHeroStatDeploy },
    { icon: Activity, label: messages.alcatrazHeroStatScale },
  ];

  const pillars = [
    { icon: Activity, title: messages.alcatrazPillar1Title, body: messages.alcatrazPillar1Body },
    { icon: Rocket, title: messages.alcatrazPillar2Title, body: messages.alcatrazPillar2Body },
    { icon: Radio, title: messages.alcatrazPillar3Title, body: messages.alcatrazPillar3Body },
    { icon: History, title: messages.alcatrazPillar4Title, body: messages.alcatrazPillar4Body },
    { icon: LockKeyhole, title: messages.alcatrazPillar5Title, body: messages.alcatrazPillar5Body },
    { icon: Bell, title: messages.alcatrazPillar6Title, body: messages.alcatrazPillar6Body },
    {
      icon: KeyRound,
      title: messages.alcatrazPillar7Title,
      body: messages.alcatrazPillar7Body,
    },
    { icon: Layers, title: messages.alcatrazPillar8Title, body: messages.alcatrazPillar8Body },
  ];

  const inspectorRows = [
    [messages.alcatrazInspectorLabelState, messages.alcatrazInspectorValueState],
    [messages.alcatrazInspectorLabelLastSeen, messages.alcatrazInspectorValueLastSeen],
    [messages.alcatrazInspectorLabelFirmware, messages.alcatrazInspectorValueFirmware],
    [messages.alcatrazInspectorLabelTemps, messages.alcatrazInspectorValueTemps],
    [messages.alcatrazInspectorLabelRam, messages.alcatrazInspectorValueRam],
    [messages.alcatrazInspectorLabelFlash, messages.alcatrazInspectorValueFlash],
    [messages.alcatrazInspectorLabelTv, messages.alcatrazInspectorValueTv],
    [messages.alcatrazInspectorLabelNetwork, messages.alcatrazInspectorValueNetwork],
    [messages.alcatrazInspectorLabelAndroid, messages.alcatrazInspectorValueAndroid],
    [messages.alcatrazInspectorLabelLocale, messages.alcatrazInspectorValueLocale],
    [messages.alcatrazInspectorLabelApps, messages.alcatrazInspectorValueApps],
  ] as const;

  type RemoteCmd = { icon: typeof Power; label: string; warning?: boolean };
  const remoteGroups: { title: string; items: RemoteCmd[] }[] = [
    {
      title: messages.alcatrazRemoteGroupControl,
      items: [
        { icon: Power, label: messages.alcatrazRemoteCmdReboot },
        { icon: Radio, label: messages.alcatrazRemoteCmdRemote },
        { icon: ImageIcon, label: messages.alcatrazRemoteCmdScreenshot },
      ],
    },
    {
      title: messages.alcatrazRemoteGroupDiagnostics,
      items: [
        { icon: Gauge, label: messages.alcatrazRemoteCmdSpeed },
        { icon: Network, label: messages.mtrAlcatrazRemoteCmd },
      ],
    },
    {
      title: messages.alcatrazRemoteGroupComms,
      items: [{ icon: Bell, label: messages.alcatrazRemoteCmdPush }],
    },
    {
      title: messages.alcatrazRemoteGroupLifecycle,
      items: [
        { icon: Rocket, label: messages.alcatrazRemoteCmdFirmware },
        { icon: TimerReset, label: messages.alcatrazRemoteCmdExclude },
      ],
    },
    {
      title: messages.alcatrazRemoteGroupLockdown,
      items: [
        { icon: ShieldOff, label: messages.alcatrazRemoteCmdBlock, warning: true },
        { icon: RotateCcw, label: messages.alcatrazRemoteCmdFactory, warning: true },
      ],
    },
  ];

  const faqs = [
    { q: messages.alcatrazFaqQ1, a: messages.alcatrazFaqA1 },
    { q: messages.alcatrazFaqQ2, a: messages.alcatrazFaqA2 },
    { q: messages.alcatrazFaqQ3, a: messages.alcatrazFaqA3 },
    { q: messages.alcatrazFaqQ4, a: messages.alcatrazFaqA4 },
    { q: messages.alcatrazFaqQ5, a: messages.alcatrazFaqA5 },
    { q: messages.alcatrazFaqQ6, a: messages.alcatrazFaqA6 },
    { q: messages.alcatrazFaqQ7, a: messages.alcatrazFaqA7 },
  ];

  const lockModes: LockMode[] = [
    {
      id: "hard",
      icon: Power,
      title: messages.alcatrazAccessProfileHardTitle,
      body: messages.alcatrazAccessProfileHardBody,
      tvBadge: messages.alcatrazAccessModeHardBadge,
      tvTitle: messages.alcatrazAccessModeHardTitle,
      tvBody: messages.alcatrazAccessModeHardBody,
      tvStatus: messages.alcatrazAccessModeHardStatus,
    },
    {
      id: "soft",
      icon: Bell,
      title: messages.alcatrazAccessProfileSoftTitle,
      body: messages.alcatrazAccessProfileSoftBody,
      tvBadge: messages.alcatrazAccessModeSoftBadge,
      tvTitle: messages.alcatrazAccessModeSoftTitle,
      tvBody: messages.alcatrazAccessModeSoftBody,
      tvStatus: messages.alcatrazAccessModeSoftStatus,
    },
    {
      id: "app",
      icon: Layers,
      title: messages.alcatrazAccessProfileAppLockTitle,
      body: messages.alcatrazAccessProfileAppLockBody,
      tvBadge: messages.alcatrazAccessModeAppLockBadge,
      tvTitle: messages.alcatrazAccessModeAppLockTitle,
      tvBody: messages.alcatrazAccessModeAppLockBody,
      tvStatus: messages.alcatrazAccessModeAppLockStatus,
    },
  ];
  const activeLockMode = lockModes.find((mode) => mode.id === selectedLockMode) ?? lockModes[0];

  useEffect(() => {
    if (!isLockModeAutoCycleActive) {
      return;
    }

    const interval = window.setInterval(() => {
      setSelectedLockMode((currentMode) => {
        const currentIndex = LOCK_MODE_IDS.indexOf(currentMode);
        return LOCK_MODE_IDS[(currentIndex + 1) % LOCK_MODE_IDS.length];
      });
    }, LOCK_MODE_AUTO_CYCLE_MS);

    return () => window.clearInterval(interval);
  }, [isLockModeAutoCycleActive]);

  const handleLockModeSelect = (mode: LockModeId) => {
    setIsLockModeAutoCycleActive(false);
    setSelectedLockMode(mode);
  };

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={home} className="hover:text-foreground">
            {messages.alcatrazBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={localizeUrl("/solutions", site.locale, href)} className="hover:text-foreground">
            {messages.alcatrazBreadcrumbSolutions}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.alcatrazBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-50"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[480px] w-[640px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:py-24">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              {messages.alcatrazHeroEyebrow}
            </div>
            <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl lg:text-5xl">
              {messages.alcatrazHeroTitle}{" "}
              <span className="text-gradient">{messages.alcatrazHeroTitleAccent}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">
              {messages.alcatrazHeroSub}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="animate-glow">
                <a href={startHeroCta}>{messages.alcatrazCtaStart}</a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href={demoCta}>{messages.alcatrazHeroCtaDemo}</a>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {heroStats.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
                >
                  <Icon className="h-3.5 w-3.5 text-primary" />
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="animate-fade-up">
            <MonitorMockup image={dashboardImg} alt={messages.alcatrazHeroImageCaption} priority />
          </div>
        </div>
      </section>

      {/* Deployment band */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.alcatrazDeploymentEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.alcatrazDeploymentTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              {messages.alcatrazDeploymentDescription}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            <DeploymentCard
              icon={Cloud}
              tag={messages.alcatrazDeploymentCloudTag}
              title={messages.alcatrazDeploymentCloudTitle}
              body={messages.alcatrazDeploymentCloudBody}
              bullets={[
                messages.alcatrazDeploymentCloudBullet1,
                messages.alcatrazDeploymentCloudBullet2,
                messages.alcatrazDeploymentCloudBullet3,
              ]}
              highlight
            />
            <DeploymentCard
              icon={ShieldCheck}
              title={messages.alcatrazDeploymentOnpremTitle}
              body={messages.alcatrazDeploymentOnpremBody}
              bullets={[
                messages.alcatrazDeploymentOnpremBullet1,
                messages.alcatrazDeploymentOnpremBullet2,
                messages.alcatrazDeploymentOnpremBullet3,
              ]}
            />
          </div>
        </div>
      </RevealSection>

      {/* Pillars */}
      <RevealSection className="border-b border-border/60 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.alcatrazPillarsEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.alcatrazPillarsTitle}
            </h2>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {pillars.map(({ icon: Icon, title, body }) => (
              <li
                key={title}
                className="rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur transition-colors hover:border-primary/30"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-sm font-semibold text-foreground">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      <DeepDiveSection
        layout="imageLeft"
        muted
        eyebrow={messages.alcatrazFleetHealthEyebrow}
        title={messages.alcatrazFleetHealthTitle}
        body={messages.alcatrazFleetHealthBody}
        bullets={[
          messages.alcatrazFleetHealthBullet1,
          messages.alcatrazFleetHealthBullet2,
          messages.alcatrazFleetHealthBullet3,
          messages.alcatrazFleetHealthBullet4,
        ]}
        placeholderLabel={messages.alcatrazFleetHealthPlaceholder}
        placeholderIcon={Activity}
        customMedia={<FleetHealthMarketingPreview messages={messages} />}
      />

      {/* Firmware deep-dive */}
      <DeepDiveSection
        layout="imageRight"
        eyebrow={messages.alcatrazFirmwareEyebrow}
        title={messages.alcatrazFirmwareTitle}
        body={messages.alcatrazFirmwareBody}
        bullets={[
          messages.alcatrazFirmwareBullet1,
          messages.alcatrazFirmwareBullet2,
          messages.alcatrazFirmwareBullet3,
          messages.alcatrazFirmwareBullet4,
        ]}
        placeholderLabel={messages.alcatrazFirmwarePlaceholder}
        placeholderIcon={Rocket}
        images={firmwareReleaseImages}
        cta={{ href: otaLink, label: messages.alcatrazFirmwareOtaLink }}
      />

      {/* Inspector deep-dive combines current state with device activity */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <DeviceInspectorMarketingPreview rows={inspectorRows} messages={messages} />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.alcatrazInspectorEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.alcatrazInspectorTitle}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {messages.alcatrazInspectorBody}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-foreground/90">
              {[
                messages.alcatrazActivityBullet1,
                messages.alcatrazActivityBullet2,
                messages.alcatrazActivityBullet3,
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </RevealSection>

      {/* Remote control deep-dive */}
      <RevealSection className="border-b border-border/60 py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-6 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.alcatrazRemoteEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.alcatrazRemoteTitle}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {messages.alcatrazRemoteBody}
            </p>
            <ul className="mt-8 space-y-3">
              {[
                messages.alcatrazRemoteHighlight1,
                messages.alcatrazRemoteHighlight2,
                messages.alcatrazRemoteHighlight3,
              ].map((text) => (
                <li key={text} className="flex items-start gap-3 text-sm text-foreground/90">
                  <span className="mt-1.5 flex h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-2xl border border-primary/20 bg-primary/[0.04] p-4 shadow-sm shadow-primary/5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Network className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                    {messages.mtrAlcatrazEyebrow}
                  </p>
                  <h3 className="mt-1 text-base font-semibold tracking-tight text-foreground">
                    {messages.mtrAlcatrazTitle}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {messages.mtrAlcatrazBody}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-5">
            {remoteGroups.map((group) => (
              <div
                key={group.title}
                className="rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.title}
                </p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {group.items.map(({ icon: Icon, label, warning }) => (
                    <li
                      key={label}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
                        warning
                          ? "border-destructive/40 bg-destructive/5 text-foreground"
                          : "border-border/60 bg-background text-foreground/90"
                      }`}
                    >
                      <Icon
                        className={`h-3.5 w-3.5 ${warning ? "text-destructive" : "text-primary"}`}
                      />
                      {label}
                      {warning && (
                        <span className="ml-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
                          {messages.alcatrazRemoteWarning}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* Device protection deep-dive */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-6 lg:grid-cols-[0.95fr_1fr]">
          <div className="lg:order-2">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.alcatrazAccessEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.alcatrazAccessTitle}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {messages.alcatrazAccessBody}
            </p>
            <LockActivationMock
              title={messages.alcatrazAccessFlowTitle}
              triggers={[
                {
                  icon: ShieldOff,
                  title: messages.alcatrazAccessManualTitle,
                  body: messages.alcatrazAccessManualBody,
                },
                {
                  icon: WifiOff,
                  title: messages.alcatrazAccessNetLockTitle,
                  body: messages.alcatrazAccessNetLockBody,
                },
              ]}
            />
          </div>
          <div className="lg:order-1">
            <TvLockMockup
              mode={activeLockMode}
              footer={messages.alcatrazAccessMockFooter}
              notice={messages.alcatrazAccessMockNotice}
              appLabel={messages.alcatrazAccessModeAppAllowedApp}
              lockedLabel={messages.alcatrazAccessModeAppLockedLabel}
            />
            <LockModeSelector
              label={messages.alcatrazAccessFlowOutcome}
              modes={lockModes}
              selectedMode={selectedLockMode}
              onSelect={handleLockModeSelect}
            />
          </div>
        </div>
      </RevealSection>

      {/* Push deep-dive */}
      <DeepDiveSection
        layout="imageRight"
        muted
        eyebrow={messages.alcatrazPushEyebrow}
        title={messages.alcatrazPushTitle}
        body={messages.alcatrazPushBody}
        bullets={[
          messages.alcatrazPushBullet1,
          messages.alcatrazPushBullet2,
          messages.alcatrazPushBullet3,
          messages.alcatrazPushBullet4,
        ]}
        placeholderLabel={messages.alcatrazPushPlaceholder}
        placeholderIcon={Bell}
        image={pushNotificationTvImg}
        mediaVariant="tv"
      />

      {/* API deep-dive */}
      <DeepDiveSection
        layout="imageLeft"
        eyebrow={messages.alcatrazApiEyebrow}
        title={messages.alcatrazApiTitle}
        body={messages.alcatrazApiBody}
        bullets={[
          messages.alcatrazApiBullet1,
          messages.alcatrazApiBullet2,
          messages.alcatrazApiBullet3,
        ]}
        placeholderLabel={messages.alcatrazApiPlaceholder}
        placeholderIcon={Network}
        image={apiIntegrationsImg}
        mediaVariant="illustration"
      />

      {/* Subscription band */}
      <RevealSection className="relative overflow-hidden border-b border-border/60 py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[420px] w-[760px] -translate-x-1/2 rounded-full opacity-30 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.alcatrazPricingEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.alcatrazPricingTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.alcatrazPricingBody}</p>
          </div>

          {/* Included tier vs paid tier */}
          <div className="mx-auto mt-12 grid max-w-4xl gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-primary/25 bg-primary/[0.05] p-6 backdrop-blur sm:p-7">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <PackageCheck className="h-6 w-6" />
              </span>
              <h3 className="mt-4 text-xl font-semibold tracking-tight text-foreground">
                {messages.alcatrazPricingIncludedTitle}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.alcatrazPricingIncludedBody}
              </p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-card/60 p-6 backdrop-blur sm:p-7">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-foreground/70">
                <Activity className="h-6 w-6" />
              </span>
              <h3 className="mt-4 text-xl font-semibold tracking-tight text-foreground">
                {messages.alcatrazPricingModelTitle}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.alcatrazPricingModelBody}
              </p>
            </div>
          </div>

          {/* What counts as an active device */}
          <div className="mx-auto mt-4 max-w-4xl rounded-2xl border border-border/60 bg-muted/20 p-6 text-center sm:p-7">
            <h3 className="text-sm font-semibold text-foreground">
              {messages.alcatrazPricingActiveTitle}
            </h3>
            <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {messages.alcatrazPricingActiveBody}
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {[
                { icon: Factory, label: messages.alcatrazPricingStateProduction },
                { icon: Truck, label: messages.alcatrazPricingStateTransit },
                { icon: Warehouse, label: messages.alcatrazPricingStateWarehouse },
                { icon: Archive, label: messages.alcatrazPricingStateRetired },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-xs font-medium text-foreground/80"
                >
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="animate-glow">
              <a href={startPricingCta}>
                {messages.alcatrazCtaStart}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href={salesCta}>{messages.alcatrazPricingCta}</a>
            </Button>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.alcatrazFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.alcatrazFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-20 text-background">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.alcatrazFinalEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.alcatrazFinalTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.alcatrazFinalBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary">
              <a href={startFinalCta}>{messages.alcatrazCtaStart}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background"
            >
              <a href={salesCta}>{messages.alcatrazFinalCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}

function LockActivationMock({
  title,
  triggers,
}: {
  title: string;
  triggers: { icon: typeof ShieldOff; title: string; body: string }[];
}) {
  return (
    <div className="alcatraz-policy-builder mt-6 overflow-hidden rounded-2xl border border-border/60 bg-card/85 shadow-xl shadow-primary/5 backdrop-blur">
      <div className="border-b border-border/60 bg-muted/20 px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
        {triggers.map(({ icon: Icon, title: triggerTitle, body }) => (
          <div
            key={triggerTitle}
            className="alcatraz-trigger-card flex gap-4 rounded-xl border border-border/55 bg-background/70 p-4"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </span>
            <div>
              <h4 className="text-base font-semibold text-foreground">{triggerTitle}</h4>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LockModeSelector({
  label,
  modes,
  selectedMode,
  onSelect,
}: {
  label: string;
  modes: LockMode[];
  selectedMode: LockModeId;
  onSelect: (mode: LockModeId) => void;
}) {
  const labelId = "alcatraz-lock-mode-selector";

  return (
    <div className="mt-5">
      <p
        id={labelId}
        className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </p>
      <div role="group" aria-labelledby={labelId} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {modes.map((mode) => {
          const Icon = mode.icon;
          const isSelected = mode.id === selectedMode;

          return (
            <button
              key={mode.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(mode.id)}
              className={`alcatraz-lock-mode-button min-w-0 cursor-pointer rounded-xl border p-3 text-left transition ${
                isSelected
                  ? "border-primary/45 bg-primary/[0.07] shadow-sm shadow-primary/10"
                  : "border-border/60 bg-card/85 hover:border-primary/30 hover:bg-primary/[0.04]"
              }`}
            >
              <span className="flex items-center gap-2">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                    isSelected ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold text-foreground">{mode.title}</span>
              </span>
              <span className="mt-2 block text-[11px] leading-relaxed text-muted-foreground">
                {mode.body}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TvLockMockup({
  mode,
  footer,
  notice,
  appLabel,
  lockedLabel,
}: {
  mode: LockMode;
  footer: string;
  notice: string;
  appLabel: string;
  lockedLabel: string;
}) {
  const ModeIcon = mode.icon;
  const statusIconClass: Record<LockModeId, string> = {
    hard: "text-red-200",
    soft: "text-sky-100",
    app: "text-emerald-100",
  };
  const ambientGlowClass: Record<LockModeId, string> = {
    hard: "bg-destructive/10",
    soft: "bg-sky-400/10",
    app: "bg-emerald-400/10",
  };
  const screenBackdropClass: Record<LockModeId, string> = {
    hard: "bg-[radial-gradient(circle_at_25%_20%,rgba(239,68,68,0.22),transparent_34%),radial-gradient(circle_at_78%_22%,rgba(14,165,233,0.18),transparent_30%),linear-gradient(135deg,rgba(15,23,42,0.92),rgba(2,6,23,0.98))]",
    soft: "bg-[radial-gradient(circle_at_22%_18%,rgba(56,189,248,0.24),transparent_35%),radial-gradient(circle_at_80%_24%,rgba(99,102,241,0.16),transparent_30%),linear-gradient(135deg,rgba(8,20,38,0.94),rgba(2,8,23,0.98))]",
    app: "bg-[radial-gradient(circle_at_24%_18%,rgba(52,211,153,0.22),transparent_34%),radial-gradient(circle_at_78%_28%,rgba(14,165,233,0.12),transparent_30%),linear-gradient(135deg,rgba(6,24,20,0.94),rgba(2,8,18,0.98))]",
  };
  const badgeClass: Record<LockModeId, string> = {
    hard: "border-red-300/25 bg-red-500/10 text-red-100",
    soft: "border-sky-200/25 bg-sky-400/10 text-sky-100",
    app: "border-emerald-200/25 bg-emerald-400/10 text-emerald-100",
  };

  return (
    <figure className="alcatraz-lock-demo relative mx-auto w-full max-w-[640px]">
      <div
        aria-hidden
        className={`absolute -inset-6 -z-10 rounded-full blur-3xl ${ambientGlowClass[mode.id]}`}
      />
      <div className="relative rounded-[1.25rem] border border-foreground/20 bg-[#070910] p-2 shadow-2xl shadow-destructive/10">
        <div className="alcatraz-lock-screen relative aspect-video overflow-hidden rounded-[0.75rem] border border-white/10 bg-[#0a1020]">
          <div aria-hidden className={`absolute inset-0 ${screenBackdropClass[mode.id]}`} />
          <div
            key={mode.id}
            className="alcatraz-tv-state relative flex h-full flex-col items-center justify-center px-4 py-2 text-center text-white sm:px-8"
          >
            <span
              className={`alcatraz-lock-badge inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wider sm:gap-2 sm:px-3 sm:py-1 sm:text-[10px] ${badgeClass[mode.id]}`}
            >
              <ModeIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              {mode.tvBadge}
            </span>

            {mode.id === "app" ? (
              <div className="mt-2 w-full max-w-sm sm:mt-4">
                <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                  <div className="col-span-2 rounded-2xl border border-emerald-300/30 bg-emerald-400/15 p-2.5 text-left shadow-lg shadow-emerald-950/20 sm:p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-300/20 text-emerald-100 sm:h-10 sm:w-10">
                      <Layers className="h-5 w-5" />
                    </div>
                    <p className="mt-2 text-sm font-semibold text-white sm:mt-3">{appLabel}</p>
                    <p className="mt-0.5 text-[9px] uppercase tracking-wider text-emerald-100/70 sm:mt-1 sm:text-[10px]">
                      {mode.tvStatus}
                    </p>
                  </div>
                  {[0, 1, 2].map((item) => (
                    <div
                      key={item}
                      className="flex min-h-20 flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-white/35 sm:min-h-24"
                    >
                      <LockKeyhole className="h-4 w-4" />
                      <span className="mt-1.5 text-[8px] font-medium sm:mt-2 sm:text-[10px]">
                        {lockedLabel}
                      </span>
                    </div>
                  ))}
                </div>
                <h3 className="mt-2 text-balance text-base font-semibold tracking-tight sm:mt-4 sm:text-2xl">
                  {mode.tvTitle}
                </h3>
                <p className="mx-auto mt-1 max-w-md text-[10px] leading-snug text-white/70 sm:mt-2 sm:text-xs sm:leading-relaxed">
                  {mode.tvBody}
                </p>
              </div>
            ) : (
              <>
                <div className="alcatraz-lock-icon mt-2 flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 shadow-lg shadow-red-950/30 backdrop-blur sm:mt-4 sm:h-14 sm:w-14 sm:rounded-2xl">
                  <ModeIcon className={`h-5 w-5 sm:h-7 sm:w-7 ${statusIconClass[mode.id]}`} />
                </div>
                <h3 className="mt-2 max-w-md text-balance text-base font-semibold tracking-tight sm:mt-4 sm:text-2xl">
                  {mode.tvTitle}
                </h3>
                <p className="mt-1 max-w-[18rem] text-[10px] leading-snug text-white/72 sm:mt-2 sm:max-w-md sm:text-xs sm:leading-relaxed">
                  {mode.tvBody}
                </p>
                {mode.id === "hard" && (
                  <div className="mt-2 w-full max-w-[16rem] sm:mt-4 sm:max-w-sm">
                    <div className="flex items-center justify-between text-[8px] font-semibold uppercase tracking-wider text-white/55 sm:text-[10px]">
                      <span>{notice}</span>
                      <span>30s</span>
                    </div>
                    <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/10 sm:mt-2 sm:h-1.5">
                      <span className="alcatraz-lock-progress block h-full rounded-full bg-red-200/80" />
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5 text-[9px] text-white/75 sm:mt-4 sm:gap-2 sm:text-[11px]">
              <span
                className={`alcatraz-lock-status items-center gap-1 rounded-full border border-white/15 bg-white/8 px-2 py-0.5 sm:inline-flex sm:gap-1.5 sm:px-2.5 sm:py-1 ${
                  mode.id === "app" ? "hidden" : "inline-flex"
                }`}
              >
                <ModeIcon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${statusIconClass[mode.id]}`} />
                {mode.tvStatus}
              </span>
              <span className="hidden rounded-full border border-white/15 bg-white/8 px-2.5 py-1 sm:inline-flex">
                {footer}
              </span>
            </div>
          </div>
        </div>
        <div
          aria-hidden
          className="absolute bottom-1 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-white/15"
        />
      </div>
      <div aria-hidden className="mx-auto -mt-0.5 flex w-[64%] justify-between px-5">
        <div className="h-6 w-10 origin-top-left -skew-x-[20deg] rounded-b bg-gradient-to-b from-[#171d28] to-[#070a10] shadow-md" />
        <div className="h-6 w-10 origin-top-right skew-x-[20deg] rounded-b bg-gradient-to-b from-[#171d28] to-[#070a10] shadow-md" />
      </div>
      <div
        aria-hidden
        className="mx-auto -mt-2 h-3 w-[76%] rounded-full bg-foreground/10 blur-sm"
      />
      <figcaption className="sr-only">{mode.tvTitle}</figcaption>
    </figure>
  );
}

function DeploymentCard({
  icon: Icon,
  title,
  body,
  bullets,
  tag,
  highlight,
}: {
  icon: typeof Cloud;
  title: string;
  body: string;
  bullets: string[];
  tag?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border p-6 backdrop-blur ${
        highlight ? "border-primary/30 bg-card/80 shadow-glow" : "border-border/60 bg-card/60"
      }`}
    >
      <div className="flex items-start justify-between">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        {tag && (
          <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-foreground/90">
            {tag}
          </span>
        )}
      </div>
      <h3 className="mt-5 text-xl font-semibold tracking-tight text-foreground">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
      <ul className="mt-5 space-y-2 text-sm text-foreground/90">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeepDiveSection({
  eyebrow,
  title,
  body,
  bullets,
  placeholderLabel,
  placeholderIcon: PlaceholderIcon,
  image,
  images,
  customMedia,
  mediaVariant = "screenshot",
  layout,
  muted,
  cta,
}: {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  placeholderLabel: string;
  placeholderIcon: typeof Rocket;
  image?: string;
  images?: readonly string[];
  customMedia?: ReactNode;
  mediaVariant?: "illustration" | "screenshot" | "tv";
  layout: "imageLeft" | "imageRight";
  muted?: boolean;
  cta?: { href: string; label: string };
}) {
  const imageLeft = layout === "imageLeft";
  const { ref, shown } = useReveal<HTMLElement>();
  return (
    <section
      ref={ref}
      className={`${revealClass(shown)} border-b border-border/60 py-20 ${
        muted ? "bg-muted/20" : "bg-background"
      }`}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
        <div className={imageLeft ? "order-2 lg:order-1" : "order-2"}>
          {customMedia ? (
            customMedia
          ) : images?.length ? (
            <ProductScreenshotCarousel images={images} alt={placeholderLabel} />
          ) : image && mediaVariant === "tv" ? (
            <TvScreenshotFrame image={image} alt={placeholderLabel} />
          ) : image && mediaVariant === "illustration" ? (
            <IntegrationIllustrationFrame image={image} alt={placeholderLabel} />
          ) : image ? (
            <ProductScreenshotFrame image={image} alt={placeholderLabel} />
          ) : (
            <div className="flex aspect-video flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/60 bg-muted/30 p-6 text-center">
              <PlaceholderIcon className="h-10 w-10 text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground/60">{placeholderLabel}</p>
            </div>
          )}
        </div>
        <div className={imageLeft ? "order-1 lg:order-2" : "order-1"}>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">{eyebrow}</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">{body}</p>
          <ul className="mt-6 space-y-2 text-sm text-foreground/90">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                {b}
              </li>
            ))}
          </ul>
          {cta && (
            <a
              href={cta.href}
              className="group mt-8 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/[0.06] px-4 py-2 text-sm font-semibold text-primary transition-colors hover:border-primary/50 hover:bg-primary/10"
            >
              {cta.label}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function DeviceInspectorMarketingPreview({
  rows,
  messages,
}: {
  rows: readonly (readonly [string, string])[];
  messages: SiteMessages;
}) {
  const previewMessages = withAlcatrazPreviewFallbacks(messages);
  const deviceActivityPreviewEvents = [
    {
      date: previewMessages.alcatrazInspectorPreviewToday,
      title: messages.alcatrazRemoteCmdFactory,
      detail: previewMessages.alcatrazInspectorPreviewCommandBy,
      time: "04:16",
      status: previewMessages.alcatrazInspectorPreviewWaiting,
      icon: RotateCcw,
      tone: "warning",
    },
    {
      date: previewMessages.alcatrazInspectorPreviewYesterday,
      title: messages.alcatrazRemoteCmdReboot,
      detail: previewMessages.alcatrazInspectorPreviewCommandBy,
      time: "16:16",
      status: previewMessages.alcatrazInspectorPreviewCompleted,
      icon: Power,
      tone: "success",
    },
    {
      date: previewMessages.alcatrazInspectorPreviewThisWeek,
      title: previewMessages.alcatrazInspectorPreviewSupportNote,
      detail: previewMessages.alcatrazInspectorPreviewPrivateNoteBy,
      time: "16:16",
      status: null,
      icon: Bell,
      tone: "primary",
    },
    {
      date: previewMessages.alcatrazInspectorPreviewLastWeek,
      title: previewMessages.alcatrazInspectorPreviewFirmwareChange,
      detail: previewMessages.alcatrazInspectorPreviewDeviceChangeApi,
      time: "16:16",
      status: null,
      icon: History,
      tone: "neutral",
    },
    {
      date: previewMessages.alcatrazInspectorPreviewEarlier,
      title: previewMessages.alcatrazInspectorPreviewOtaCompleted,
      detail: previewMessages.alcatrazInspectorPreviewOtaMetrics,
      time: "16:16",
      status: null,
      icon: Rocket,
      tone: "success",
    },
  ] as const;
  const [activeTab, setActiveTab] = useState<"overview" | "activity">("activity");
  const overviewPanelRef = useRef<HTMLDivElement>(null);
  const activityPanelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);

  useEffect(() => {
    const panel = activeTab === "overview" ? overviewPanelRef.current : activityPanelRef.current;

    if (!panel) {
      return;
    }

    const updateHeight = () => setPanelHeight(panel.scrollHeight);
    const frame = window.requestAnimationFrame(updateHeight);
    const observer = new ResizeObserver(updateHeight);
    observer.observe(panel);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [activeTab]);

  return (
    <figure className="relative mx-auto w-full max-w-[620px]">
      <div aria-hidden className="absolute -inset-5 -z-10 rounded-[2rem] bg-primary/12 blur-3xl" />
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (value === "overview" || value === "activity") {
            setActiveTab(value);
          }
        }}
        className="overflow-hidden rounded-2xl border border-border/70 bg-card/95 shadow-2xl shadow-primary/10 backdrop-blur"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border/60 bg-gradient-to-r from-primary/[0.08] via-card to-card px-4 py-3.5 sm:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-semibold text-foreground">TV5 Ultra</p>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {previewMessages.alcatrazInspectorPreviewOnline}
              </span>
            </div>
            <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
              53:38:EC:FE:FC:BE · living-room-0421
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {messages.alcatrazInspectorLabelFirmware}
            </p>
            <p className="text-xs font-semibold text-foreground">1.9.1</p>
          </div>
        </div>

        <TabsList className="grid h-auto w-full grid-cols-2 rounded-none border-b border-border/60 bg-muted/20 p-1.5">
          <TabsTrigger
            value="overview"
            className="gap-1.5 rounded-lg py-2 text-[13px] data-[state=active]:bg-background data-[state=active]:text-primary"
          >
            <Gauge className="h-3.5 w-3.5" />
            {previewMessages.alcatrazInspectorPreviewCurrentState}
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="gap-1.5 rounded-lg py-2 text-[13px] data-[state=active]:bg-background data-[state=active]:text-primary"
          >
            <History className="h-3.5 w-3.5" />
            {previewMessages.alcatrazInspectorPreviewActivity}
          </TabsTrigger>
        </TabsList>

        <div
          className="overflow-hidden transition-[height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
          style={panelHeight === null ? undefined : { height: `${panelHeight}px` }}
        >
          <TabsContent forceMount value="overview" className="m-0 data-[state=inactive]:hidden">
            <div ref={overviewPanelRef} className="px-4 pb-4 sm:px-5 sm:pb-5">
              <div className="flex items-center justify-between border-b border-border/60 py-3">
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    {previewMessages.alcatrazInspectorPreviewCondition}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {previewMessages.alcatrazInspectorPreviewLastChecked.replace("{minutes}", "12")}
                  </p>
                </div>
                <span className="rounded-full bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                  {previewMessages.alcatrazFleetPreviewHealthy}
                </span>
              </div>
              <DeviceStateTable rows={rows} compact />
            </div>
          </TabsContent>

          <TabsContent forceMount value="activity" className="m-0 data-[state=inactive]:hidden">
            <div ref={activityPanelRef}>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 bg-muted/15 px-4 py-2.5 sm:px-5">
                <div className="flex flex-wrap gap-1.5">
                  <span className="rounded-md bg-primary px-2 py-1 text-[10px] font-semibold text-primary-foreground">
                    {previewMessages.alcatrazInspectorPreviewAllEvents}
                  </span>
                  <span className="rounded-md border border-border/60 bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground">
                    {previewMessages.alcatrazInspectorPreviewCommands}
                  </span>
                  <span className="rounded-md border border-border/60 bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground">
                    {messages.alcatrazInspectorLabelFirmware}
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                  <Layers className="h-3 w-3" /> {previewMessages.alcatrazInspectorPreviewNewest}
                </span>
              </div>

              <div className="px-4 pb-3 sm:px-5">
                {deviceActivityPreviewEvents.map((event) => {
                  const Icon = event.icon;
                  const iconClass =
                    event.tone === "warning"
                      ? "bg-amber-500/10 text-amber-600 dark:text-amber-300"
                      : event.tone === "success"
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
                        : event.tone === "primary"
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground";

                  return (
                    <div key={`${event.date}-${event.title}`}>
                      <p className="border-b border-border/50 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                        {event.date}
                      </p>
                      <div className="grid grid-cols-[30px_minmax(0,1fr)_auto] items-center gap-2.5 py-2.5">
                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-full ${iconClass}`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-semibold text-foreground">
                            {event.title}
                          </p>
                          <p className="truncate text-[11px] text-muted-foreground">
                            {event.detail}
                          </p>
                        </div>
                        <div className="pl-1 text-right">
                          {event.status && (
                            <p
                              className={`mb-0.5 whitespace-nowrap text-[10px] font-semibold ${
                                event.tone === "warning"
                                  ? "text-amber-700 dark:text-amber-300"
                                  : "text-emerald-700 dark:text-emerald-300"
                              }`}
                            >
                              {event.status}
                            </p>
                          )}
                          <p className="text-[10px] text-muted-foreground">{event.time}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-border/60 bg-muted/20 px-4 py-2.5 text-[10px] sm:px-5">
                <span className="truncate font-medium text-foreground">
                  {previewMessages.alcatrazInspectorPreviewFooter}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {previewMessages.alcatrazInspectorPreviewIllustrative}
                </span>
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>
      <figcaption className="sr-only">{previewMessages.alcatrazInspectorPreviewAlt}</figcaption>
    </figure>
  );
}

function DeviceStateTable({
  rows,
  compact = false,
}: {
  rows: readonly (readonly [string, string])[];
  compact?: boolean;
}) {
  return (
    <dl className={`${compact ? "mt-0" : "mt-3"} divide-y divide-border/60 text-sm`}>
      {rows.map(([label, value]) => (
        <div
          key={label}
          className={
            compact
              ? "grid grid-cols-1 gap-0.5 py-2 sm:grid-cols-[128px_1fr] sm:gap-3"
              : "grid grid-cols-[140px_1fr] gap-3 py-2.5"
          }
        >
          <dt
            className={`${compact ? "text-[10px] font-medium" : "text-xs"} uppercase tracking-wide text-muted-foreground`}
          >
            {label}
          </dt>
          <dd
            className={
              compact ? "text-xs leading-relaxed text-foreground/90" : "text-foreground/90"
            }
          >
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function FleetHealthMarketingPreview({ messages }: { messages: SiteMessages }) {
  const previewMessages = withAlcatrazPreviewFallbacks(messages);

  return (
    <figure aria-hidden="true" className="relative mx-auto w-full max-w-[620px] select-none">
      <div className="absolute -inset-5 -z-10 rounded-[2rem] bg-primary/12 blur-3xl" />
      <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/95 shadow-2xl shadow-primary/10 backdrop-blur">
        <div className="flex items-center justify-between border-b border-border/60 bg-gradient-to-r from-primary/[0.08] via-card to-card px-4 py-3.5 sm:px-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Activity className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {messages.alcatrazFleetHealthEyebrow}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {previewMessages.alcatrazFleetPreviewUpdated}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {previewMessages.alcatrazFleetPreviewLive}
          </span>
        </div>

        <div className="p-3.5 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-[132px_1fr] sm:items-center">
            <div className="flex justify-center">
              <div
                className="h-28 w-28 rounded-full p-[11px] shadow-inner"
                style={{
                  background:
                    "conic-gradient(var(--destructive) 0 2%, #f59e0b 2% 6%, var(--border) 6% 100%)",
                }}
              >
                <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-card shadow-sm">
                  <span className="text-2xl font-semibold tracking-tight text-foreground">36</span>
                  <span className="w-[78px] text-center text-[9px] font-semibold uppercase leading-tight tracking-wide text-muted-foreground">
                    {previewMessages.alcatrazFleetPreviewNeedsAttention}
                  </span>
                  <span className="mt-0.5 text-[10px] font-medium text-muted-foreground">6.0%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-red-300/60 bg-red-50/80 p-2.5 dark:border-red-500/25 dark:bg-red-500/10">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-300">
                  {previewMessages.alcatrazFleetPreviewCallFirst}
                </p>
                <p className="mt-0.5 text-xl font-semibold leading-none text-red-700 dark:text-red-300">
                  12
                </p>
              </div>
              <div className="rounded-xl border border-amber-300/70 bg-amber-50/80 p-2.5 dark:border-amber-500/25 dark:bg-amber-500/10">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                  {previewMessages.alcatrazFleetPreviewKeepWatch}
                </p>
                <p className="mt-0.5 text-xl font-semibold leading-none text-amber-700 dark:text-amber-300">
                  24
                </p>
              </div>
              <div className="rounded-xl border border-emerald-300/60 bg-emerald-50/80 p-2.5 dark:border-emerald-500/25 dark:bg-emerald-500/10">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  {previewMessages.alcatrazFleetPreviewHealthy}
                </p>
                <p className="mt-0.5 text-xl font-semibold leading-none text-emerald-700 dark:text-emerald-300">
                  564
                </p>
              </div>
              <div className="rounded-xl border border-border/70 bg-muted/35 p-2.5">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {previewMessages.alcatrazFleetPreviewMonitored}
                </p>
                <p className="mt-0.5 text-xl font-semibold leading-none text-foreground">600</p>
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-xl border border-border/60 bg-muted/35 p-2">
            <span className="mr-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
              {previewMessages.alcatrazFleetPreviewIssueTypes}
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-1 text-[10px] font-medium text-foreground shadow-sm ring-1 ring-border/60">
              <WifiOff className="h-3 w-3 text-red-500" />
              {previewMessages.alcatrazFleetPreviewWeakWifi}{" "}
              <strong className="font-semibold text-muted-foreground">30</strong>
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-1 text-[10px] font-medium text-foreground shadow-sm ring-1 ring-border/60">
              <Thermometer className="h-3 w-3 text-amber-500" />
              {previewMessages.alcatrazFleetPreviewTemperature}{" "}
              <strong className="font-semibold text-muted-foreground">12</strong>
            </span>
          </div>

          <div className="mt-3 overflow-hidden rounded-xl border border-border/60 bg-background/70">
            <div className="flex items-center justify-between border-b border-border/60 px-3 py-2.5">
              <div>
                <p className="text-[10px] font-semibold text-foreground">
                  {previewMessages.alcatrazFleetPreviewQueue}
                </p>
                <p className="text-[9px] text-muted-foreground">
                  {previewMessages.alcatrazFleetPreviewCriticalFirst}
                </p>
              </div>
              <span className="rounded-full bg-red-500/10 px-2 py-1 text-[9px] font-semibold text-red-600 dark:text-red-300">
                {previewMessages.alcatrazFleetPreviewShown.replace("{count}", "3")}
              </span>
            </div>
            <div className="divide-y divide-border/50">
              {fleetHealthPreviewIssues.map(
                ({ model, issue, reading, ageMinutes, icon: Icon, critical }) => (
                  <div key={`${model}-${issue}`} className="flex items-center gap-2.5 px-3 py-2.5">
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                        critical
                          ? "bg-red-500/10 text-red-500"
                          : "bg-amber-500/10 text-amber-600 dark:text-amber-300"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[11px] font-semibold text-foreground">{model}</p>
                      <p className="truncate text-[9px] text-muted-foreground">
                        {issue === "weakWifi"
                          ? previewMessages.alcatrazFleetPreviewWeakWifi
                          : previewMessages.alcatrazFleetPreviewHighTemperature}{" "}
                        ·{" "}
                        {previewMessages.alcatrazPreviewMinutesAgo.replace(
                          "{minutes}",
                          String(ageMinutes),
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-[11px] font-semibold ${
                          critical
                            ? "text-red-600 dark:text-red-300"
                            : "text-amber-700 dark:text-amber-300"
                        }`}
                      >
                        {reading}
                      </p>
                      <p className="text-[9px] text-muted-foreground">
                        {critical
                          ? previewMessages.alcatrazFleetPreviewCallFirst
                          : previewMessages.alcatrazFleetPreviewWatch}
                      </p>
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                  </div>
                ),
              )}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-dashed border-amber-400/50 bg-amber-50/70 px-3 py-2 text-[9px] dark:bg-amber-500/10">
            <span className="inline-flex items-center gap-1.5 font-medium text-amber-800 dark:text-amber-200">
              <RotateCcw className="h-3 w-3" />
              {previewMessages.alcatrazFleetPreviewAwaiting.replace("{count}", "6")}
            </span>
            <span className="text-muted-foreground">
              {previewMessages.alcatrazPreviewIllustrativeData}
            </span>
          </div>
        </div>
      </div>
    </figure>
  );
}

function IntegrationIllustrationFrame({ image, alt }: { image: string; alt: string }) {
  return (
    <Dialog>
      <figure className="relative">
        <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label={alt}
            className="group relative block w-full overflow-hidden rounded-2xl border border-border/60 bg-[#f7faff] shadow-xl shadow-primary/10 transition duration-200 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-primary/15 focus:outline-none focus:ring-2 focus:ring-primary/45 focus:ring-offset-2"
          >
            <img
              src={image}
              alt={alt}
              loading="lazy"
              className="block aspect-[1500/938] w-full object-cover transition duration-300 group-hover:scale-[1.015]"
            />
            <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-background/85 text-foreground shadow-lg shadow-primary/10 backdrop-blur transition group-hover:bg-background">
              <Maximize2 className="h-4 w-4" />
            </span>
          </button>
        </DialogTrigger>
        <figcaption className="sr-only">{alt}</figcaption>
      </figure>
      <DialogContent className="max-h-[94vh] max-w-[min(96vw,1500px)] overflow-hidden border-0 bg-transparent p-0 shadow-none sm:rounded-2xl">
        <DialogTitle className="sr-only">{alt}</DialogTitle>
        <DialogDescription className="sr-only">{alt}</DialogDescription>
        <div className="overflow-auto rounded-2xl border border-border/70 bg-[#f7faff] shadow-2xl shadow-black/30">
          <img
            src={image}
            alt=""
            className="block min-w-[900px] object-cover md:min-w-0 md:w-full"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TvScreenshotFrame({ image, alt }: { image: string; alt: string }) {
  return (
    <figure className="relative mx-auto max-w-[720px]">
      <div aria-hidden className="absolute -inset-6 -z-10 rounded-full bg-primary/15 blur-3xl" />
      <div className="relative rounded-[1.25rem] border border-foreground/20 bg-[#06080d] p-2 shadow-2xl shadow-primary/10">
        <div className="overflow-hidden rounded-[0.75rem] border border-white/10 bg-black">
          <img
            src={image}
            alt={alt}
            loading="lazy"
            className="block aspect-video w-full object-cover"
          />
        </div>
        <div
          aria-hidden
          className="absolute bottom-1 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-white/15"
        />
      </div>
      <div aria-hidden className="mx-auto -mt-0.5 flex w-[64%] justify-between px-5">
        <div className="h-6 w-10 origin-top-left -skew-x-[20deg] rounded-b bg-gradient-to-b from-[#171d28] to-[#070a10] shadow-md" />
        <div className="h-6 w-10 origin-top-right skew-x-[20deg] rounded-b bg-gradient-to-b from-[#171d28] to-[#070a10] shadow-md" />
      </div>
      <div
        aria-hidden
        className="mx-auto -mt-2 h-3 w-[76%] rounded-full bg-foreground/10 blur-sm"
      />
      <figcaption className="sr-only">{alt}</figcaption>
    </figure>
  );
}

function ProductScreenshotCarousel({ images, alt }: { images: readonly string[]; alt: string }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(images.length);
  const [paused, setPaused] = useState(false);
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!api) {
      return;
    }

    const update = () => {
      setCurrent(api.selectedScrollSnap());
      setCount(api.scrollSnapList().length);
    };

    update();
    api.on("select", update);
    api.on("reInit", update);

    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  useEffect(() => {
    if (!api || paused || count <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      const root = rootRef.current;

      if (
        root?.matches(":hover") ||
        (document.activeElement && root?.contains(document.activeElement))
      ) {
        return;
      }

      api.scrollNext();
    }, 4200);

    return () => {
      window.clearInterval(timer);
    };
  }, [api, count, paused]);

  return (
    <figure
      ref={rootRef}
      className="relative"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onPointerMove={() => setPaused(true)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
      <div className="rounded-2xl border border-border/60 bg-card/80 p-2 shadow-xl shadow-primary/10 backdrop-blur">
        <Carousel setApi={setApi} opts={{ align: "start", loop: true }}>
          <CarouselContent className="-ml-0">
            {images.map((image) => (
              <CarouselItem key={image} className="pl-0">
                <div className="flex aspect-[3/2] items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-white">
                  <img
                    src={image}
                    alt={alt}
                    loading="lazy"
                    className="block max-h-full w-full object-contain"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-3 top-1/2 border-border/70 bg-background/90 shadow-lg backdrop-blur hover:bg-background disabled:opacity-30" />
          <CarouselNext className="right-3 top-1/2 border-border/70 bg-background/90 shadow-lg backdrop-blur hover:bg-background disabled:opacity-30" />
        </Carousel>
      </div>
      <div className="mt-3 flex justify-center gap-2">
        {Array.from({ length: count }).map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`${alt} ${index + 1}`}
            aria-current={current === index}
            onClick={() => api?.scrollTo(index)}
            className={`h-2 rounded-full transition-all ${
              current === index ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>
      <figcaption className="sr-only">{alt}</figcaption>
    </figure>
  );
}

function ProductScreenshotFrame({ image, alt }: { image: string; alt: string }) {
  return (
    <figure className="relative">
      <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
      <div className="rounded-2xl border border-border/60 bg-card/80 p-2 shadow-xl shadow-primary/10 backdrop-blur">
        <div className="overflow-hidden rounded-xl border border-border/60 bg-background">
          <img
            src={image}
            alt={alt}
            loading="lazy"
            className="block aspect-[1600/1188] w-full object-cover object-top"
          />
        </div>
      </div>
      <figcaption className="sr-only">{alt}</figcaption>
    </figure>
  );
}
